import React, { useState, useEffect } from 'react';
import { TextField, MenuItem, Button, Box } from '@mui/material';
import { API_URL } from "../lib/utils";
import { useNavigate } from 'react-router-dom';

const DEFAULT_PROMPT = {
    _id: 'default',
    title: 'Default',
    content: 'Given the following transcription, Re-write the content in detailed structured way.'
};

const PromptsSelect = ({ value, onChange }) => {
    const [prompts, setPrompts] = useState([]);
    const [promptText, setPromptText] = useState(DEFAULT_PROMPT.content);
    const [selectedPredefinedPrompt, setSelectedPredefinedPrompt] = useState('default');
    const navigate = useNavigate();

    useEffect(() => {
        fetchPrompts();
    }, []);

    // Initialize prompt text when value changes from parent
    useEffect(() => {
        if (value?.content !== undefined && value.content !== promptText) {
            setPromptText(value.content);
            // If the value matches the default prompt, select it in the dropdown
            if (value._id === 'default') {
                setSelectedPredefinedPrompt('default');
            }
        }
    }, [value?.content, value?._id, promptText]);

    const fetchPrompts = async () => {
        const response = await fetch(`${API_URL}/api/prompts`, {
            credentials: 'include',
            headers: {
                'Accept': 'application/json'
            }
        });
        const data = await response.json();
        setPrompts(data);
    };

    const handlePromptTextChange = (event) => {
        const newText = event.target.value;
        setPromptText(newText);
        
        // Create a prompt object with the new text
        const customPrompt = {
            _id: 'custom',
            title: 'Custom Prompt',
            content: newText
        };
        onChange(customPrompt);
        
        // Clear predefined selection if user is typing something different
        if (selectedPredefinedPrompt === 'default' && newText !== DEFAULT_PROMPT.content) {
            setSelectedPredefinedPrompt('');
        } else if (selectedPredefinedPrompt && selectedPredefinedPrompt !== 'default' && newText !== prompts.find(p => p._id === selectedPredefinedPrompt)?.content) {
            setSelectedPredefinedPrompt('');
        }
    };

    const handlePredefinedPromptSelect = (event) => {
        const promptId = event.target.value;
        setSelectedPredefinedPrompt(promptId);
        
        if (promptId === 'default') {
            setPromptText(DEFAULT_PROMPT.content);
            onChange(DEFAULT_PROMPT);
        } else if (promptId) {
            const selectedPrompt = prompts.find(p => p._id === promptId);
            if (selectedPrompt) {
                setPromptText(selectedPrompt.content);
                onChange(selectedPrompt);
            }
        } else {
            // If empty selection, clear the dropdown but keep the text
            setSelectedPredefinedPrompt('');
        }
    };

    return (
        <Box>
            <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                    select
                    label="Load predefined prompt"
                    value={selectedPredefinedPrompt}
                    onChange={handlePredefinedPromptSelect}
                    size="small"
                    sx={{ minWidth: 200, flexGrow: 1 }}
                >
                    <MenuItem value="default">
                        Default
                    </MenuItem>
                    {prompts.map((prompt) => (
                        <MenuItem key={prompt._id} value={prompt._id}>
                            {prompt.title}
                        </MenuItem>
                    ))}
                    <MenuItem value="">
                        <em>Custom prompt</em>
                    </MenuItem>
                </TextField>
                <Button 
                    variant="outlined"
                    size="small"
                    onClick={() => navigate('/prompts')}
                >
                    Manage
                </Button>
            </Box>

            <TextField
                multiline
                minRows={4}
                maxRows={12}
                label="Summarization prompt"
                value={promptText}
                onChange={handlePromptTextChange}
                placeholder="Enter your summarization instructions here, or select a predefined prompt above..."
                required
                fullWidth
                helperText={
                    selectedPredefinedPrompt === 'default'
                        ? "Using default prompt"
                        : selectedPredefinedPrompt 
                            ? `Using predefined prompt: ${prompts.find(p => p._id === selectedPredefinedPrompt)?.title || ''}`
                            : "Describe how you want the audio to be summarized"
                }
                variant="outlined"
            />
        </Box>
    );
};

export default PromptsSelect;
